/* @flow */

import * as React from 'react';
import {
  mapNotificationPath,
  getNotificationExcludePaths,
  orderNotificationsByPriority,
  getNotificationIncludePaths,
  getNotificationId,
  getNotificationIsActive,
} from 'src/shared/app/notification/util/notificationUtil';
import useReduxState from 'src/shared/app/base/hook/useReduxState';
import useRouteMatch from 'src/shared/app/base/route/hook/useRouteMatch';
import { getRoutePath } from 'src/shared/app/base/route/util/routeUtils';

import type { RouteData } from 'src/shared/app/base/route/type/routeTypes';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';
import NotificationContainerItem from 'src/shared/app/notification/component/NotificationContainerItem';

type Props = {
  queue: Array<Notification<>>,
};

export default function NotificationContainerDumb({
  queue,
}: Props): React.Node {
  const notificationsOrdered = orderNotificationsByPriority(queue);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */
  const activeRoute = useRouteMatch();
  const state = useReduxState();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const showMainNotification = (notification) => {
    if (!getNotificationIsActive(notification)) {
      return false;
    }

    const excludePaths = getNotificationExcludePaths(notification);
    const includePaths = getNotificationIncludePaths(notification);

    const getRoutesElt = (values: Array<string | RouteData>): string[] =>
      values
        .map<string>((route) => mapNotificationPath(route, notification, state))
        .filter(Boolean);

    const excludeRoutesElt = getRoutesElt(excludePaths);
    const includeRoutesElt = getRoutesElt(includePaths);

    const isActiveRouteIncluded = (routes: string[]): boolean =>
      routes.some((route) => route === getRoutePath(activeRoute, state));

    return (
      !isActiveRouteIncluded(excludeRoutesElt) &&
      (includeRoutesElt.length === 0 || isActiveRouteIncluded(includeRoutesElt))
    );
  };

  const firstShowedNotificationIndex = notificationsOrdered.findIndex(
    (notification) => showMainNotification(notification),
  );

  return (
    <>
      {notificationsOrdered.map((notification, index) => (
        <NotificationContainerItem
          key={getNotificationId(notification)}
          notification={notification}
          isVisible={firstShowedNotificationIndex === index}
        />
      ))}
    </>
  );
}
